import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { AUDIT_LIST } from "./audits";
import { formatLocalPrice, useCurrency } from "./currency";
import { CurrencySwitcher, PriceNote } from "./CurrencySwitcher";

const AuditCard = ({ a, i, currency }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.5, delay: i * 0.07 }}
    className="flex"
  >
    <Link
      to={a.path}
      data-testid={`audit-teaser-card-${a.key}`}
      className="group flex flex-col w-full border border-paper/20 hover:border-paper p-6 md:p-7 transition-colors"
      style={{ borderTop: `4px solid ${a.accent}` }}
    >
      <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-paper/60">
        <span>Audit · 0{i + 1}</span>
        <span>Sample score {a.sample.score}/100</span>
      </div>
      <h3 className="mt-5 font-heading font-black text-2xl md:text-3xl tracking-tight">{a.platform}</h3>
      <ul className="mt-4 space-y-2 text-[14px] text-paper/70 flex-1">
        {a.included.slice(2, 5).map(([, t]) => (
          <li key={t} className="flex gap-2"><span className="text-signal">—</span>{t}</li>
        ))}
      </ul>
      <div className="mt-6 pt-5 border-t border-paper/15 flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50">Audit from</div>
          <div className="font-heading font-black text-2xl" data-testid={`audit-teaser-price-${a.key}`}>{formatLocalPrice(a.tiers[1].usd, currency)}</div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-paper/50 mr-auto ml-4 mb-1 hidden xl:inline">Free check available</span>
        <ArrowUpRight className="text-paper group-hover:text-signal group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" size={22} />
      </div>
    </Link>
  </motion.div>
);

export default function AuditTeaser() {
  const { currency } = useCurrency();
  return (
    <section id="audit-teaser" data-testid="audit-teaser" className="py-14 md:py-20 bg-ink text-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 02½ — Account audits</span>
          <span className="flex-1 h-px bg-paper/20" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-paper/60">4 platforms</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-end mb-10 md:mb-12">
          <h2 className="md:col-span-7 font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest">
            Find wasted spend. <span className="text-signal">Fix what&apos;s broken.</span>
          </h2>
          <div className="md:col-span-5">
            <p className="text-base md:text-lg text-paper/70 leading-relaxed">
              Deep-dive audits of your LinkedIn, Google, Meta or Bing Ads account — structure, targeting,
              creative, tracking and spend — with a prioritized roadmap. Every platform starts with a free
              health check.
            </p>
            <Link
              to="/audits"
              data-testid="audit-teaser-cta"
              className="mt-6 inline-flex items-center gap-3 bg-paper text-ink hover:bg-signal hover:text-paper px-7 py-4 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
            >
              Compare all audits <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="audit-teaser-grid">
          {AUDIT_LIST.map((a, i) => <AuditCard key={a.key} a={a} i={i} currency={currency} />)}
        </div>
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CurrencySwitcher testId="teaser-currency-switcher" dark />
          <PriceNote dark />
        </div>
      </div>
    </section>
  );
}
